import { Component, EventEmitter, Input, OnInit, ViewChild } from '@angular/core';
import { Company, PurchaseOrderDto } from '@app/models';
import { FinancialReportDto } from '@app/models/financial-report.dto';
import { PoFilesComponent } from '@app/modules/po-details/components/po-files/po-files.component';
import { CompanyService, FileService, Toaster } from '@app/services';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { LasgroFinancial_report_document, LasgroPanda_doc_document } from 'api/models';
import { FinancialReportService } from 'api/services';
import { PDFDocument } from 'pdf-lib';
import Swal from 'sweetalert2';
import saveAs from 'file-saver'
import { forkJoin, from, Observable, of } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { FinancialReportSignerDto } from '@app/models/financial-report-signer.dto';
import * as moment from 'moment';
import { HttpClient } from '@angular/common/http';
import { concatMap, map, mergeMap, reduce } from 'rxjs/operators';
@Component({
  selector: 'voucher-generation-modal',
  templateUrl: './voucher-generation-modal.component.html',
  styleUrls: ['./voucher-generation-modal.component.scss']
})
export class VoucherGenerationModalComponent implements OnInit {
  @Input() purchaseOrder: PurchaseOrderDto
  @Input() financialReport: FinancialReportDto
  @Input() companyType: string;
  @Input() supplier: Company;
  refreshSubmittedVoucherDocuments: EventEmitter<any> = new EventEmitter<any>();
  addSubmittedVoucherFileEventEmitter: EventEmitter<any> = new EventEmitter<any>();
  pdfSrc: string = ''
  file: File
  mergedFile: any
  isMerged = false;
  isSignaturesAdded = false;
  loading = false;
  fileName = ''
  invalidFileName = ''
  errorMessage: string = '';
  voucherPeriod: string = '';
  @ViewChild('filesSimplePO', { static: false }) filesSimplePO: PoFilesComponent
  constructor(
    private ngbActiveModal: NgbActiveModal,
    private $company: CompanyService,
    private financialReportService: FinancialReportService,
    private $toaster: Toaster,
    private fileService: FileService,
    private domSanitizer: DomSanitizer,
    private http: HttpClient
  ) { }

  ngOnInit(): void {
    const formattedDate = moment().format('MM/DD/YYYY HH:mm');
    let currentReportColumn = this.financialReport.financialReportType.financialReportColumns.find(
      (x) => x.order == this.financialReport.currentColumn,
    );
    this.fileName = `(${formattedDate}) Saved Voucher file state in period (${currentReportColumn.name}) for vendor (${this.supplier.name}).pdf`
    
    // Set default voucher period to current column name
    this.voucherPeriod = currentReportColumn.name;
  }

  closeModal() {
    this.ngbActiveModal.dismiss();
  }

  sendVoucher(file) {
    this.financialReportService
      .financialReportSendVoucher({
        purchaseOrderID: this.purchaseOrder.id,
        financialReportID: this.financialReport.id,
        fileName: this.fileName,
        file,
        voucherPeriod: this.voucherPeriod
      })
      .subscribe((document: LasgroPanda_doc_document) => {
        this.$toaster.show('success', '', `The voucher was sent to the supplier for signature`);
      });
  }

  async addFile() {
    if (this.fileName && !this.fileName.toLowerCase().endsWith('.pdf')) {
      this.invalidFileName = 'The file name must contain .pdf';
      return
    } else {
      this.invalidFileName = ''
    }
    const formData: FormData = new FormData();
    formData.append('file', this.file, this.fileName);
    this.$company
      .addFileToPo(
        this.purchaseOrder.customer_id,
        this.purchaseOrder.id,
        formData,
        false,
        false,
        '$submitted_voucher_documents',
        true,
        'N/A',
      )
      .subscribe(
        (_) => {
          this.$toaster.show('success', 'Success', 'File has been added');
        },
        (error) => {
          console.error(error);
          const errMsg =
            error.error && error.error.detail ? error.error.detail : 'An error ocurred while uploading a file.';
          this.$toaster.show('error', 'File upload', errMsg);
        },
      );
  }

  downloadFile(fileUrl: string) {
    this.http.get(fileUrl, { responseType: 'blob' })
  }

  async mergeFiles() {
    let selectedFiles = this.filesSimplePO.getSelectedFilesForVoucherModal()
    if (selectedFiles.length == 0) {
      Swal.fire('Should be selected at least 1 file. ')

      this.loading = false;
      return;
    }
    this.loading = true;
    let blobFilesRequests = []
    selectedFiles.forEach(x => {
      if (x.type == 'file') {
        blobFilesRequests.push(this.fileService.downloadFile(x.item.file_id))
      } else if (x.type == 'document') {
        blobFilesRequests.push(this.fileService.financialReportDocumentDownload({
          purchaseOrderID: this.purchaseOrder.id,
          organizationID: this.purchaseOrder.customer_id,
          financialReportDocumentID: x.item.pandaDocId,
        }).pipe(
          map((blob: Blob) => {
            // Create a File from the Blob, provide the file name and optional mime type
            return new File([blob], `${x.item.pandaDocId}.pdf`, { type: 'application/pdf' });
          })
        ))
      }

    })
    from(blobFilesRequests)
      .pipe(
        concatMap(request => request), // Process requests one by one
        reduce((acc, response) => {
          acc.push(response); // Collect each response into an array
          return acc;
        }, []) // Initialize with an empty array
      ).subscribe((results) => {
        let files = [this.file, ...results] as File[]
        if (this.areAllFilesPDF(files)) {
          this.mergePDFs(files).subscribe((result) => {

            // Create a Blob from the merged PDF
            this.mergedFile = new Blob([result], { type: 'application/pdf' });
            this.pdfSrc = this.domSanitizer.bypassSecurityTrustResourceUrl(
              URL.createObjectURL(this.mergedFile),
            ) as string;
            this.isMerged = true;
            this.loading = false;
          });
        } else {
          Swal.fire('Please select only PDF files.');
          this.loading = false;
        }
      })
  }

  addSignatures() {
    return new Observable<Blob>((observer) => {
      setTimeout(async () => {
        let fileBuffer = await this.file.arrayBuffer();
        const pdfDoc = await PDFDocument.load(fileBuffer);

        if (this.financialReport.financialReportSigners.some((x: FinancialReportSignerDto) => !x.isAdded)) {
          Swal.fire('Not all signers added to financial report')
          this.loading = false;
          return;
        }
        let fontSize = 7;
        const form = pdfDoc.getForm();
        const signatureFiield1 = form.getField('SIGNATURE');
        if (signatureFiield1) {
          const position = signatureFiield1.acroField.getWidgets()[0].getRectangle();
          form.removeField(signatureFiield1);
          const page = pdfDoc.getPage(0);
          if (this.financialReport.financialReportSigners.some((x: FinancialReportSignerDto) => x.isAdded && x.signatureField == 'SIGNATURE')) {
            let code = this.financialReport.financialReportSigners.find((x: FinancialReportSignerDto) => x.isAdded && x.signatureField == 'SIGNATURE')
              ?.signer.code;
            page.drawText(code, { x: position.x, y: position.y, size: fontSize });
          }
        }

        const signatureFiield2 = form.getField('SIGNATURE1');
        if (signatureFiield2) {
          const position = signatureFiield2.acroField.getWidgets()[0].getRectangle();
          form.removeField(signatureFiield2);
          const page = pdfDoc.getPage(0);
          if (this.financialReport.financialReportSigners.some((x: FinancialReportSignerDto) => x.isAdded && x.signatureField == 'SIGNATURE1')) {
            let code = this.financialReport.financialReportSigners.find((x: FinancialReportSignerDto) => x.isAdded && x.signatureField == 'SIGNATURE1')
              ?.signer.code;
            page.drawText(code, { x: position.x, y: position.y, size: fontSize });
          }
        }
        const signatureFiield3 = form.getField('SIGNATURE2');
        if (signatureFiield3) {
          const position = signatureFiield3.acroField.getWidgets()[0].getRectangle();
          form.removeField(signatureFiield3);
          const page = pdfDoc.getPage(0);
          if (this.financialReport.financialReportSigners.some((x: FinancialReportSignerDto) => x.isAdded && x.signatureField == 'SIGNATURE2')) {
            let code = this.financialReport.financialReportSigners.find((x: FinancialReportSignerDto) => x.isAdded && x.signatureField == 'SIGNATURE2')
              ?.signer.code;
            page.drawText(code, { x: position.x, y: position.y, size: fontSize });
          }
        }
        fileBuffer = (await pdfDoc.save()).buffer;

        var result = new Blob([fileBuffer], { type: 'application/pdf' });
        observer.next(result);
        observer.complete();
        return result;
      }, 10)
    })
  }

  onAddSignaturesClick() {
    this.loading = true;
    this.addSignatures().subscribe((file: Blob) => {
      this.file = new File([file], 'template.pdf', { type: 'application/pdf' });
      this.loading = false;
      this.isSignaturesAdded = true;
      this.pdfSrc = this.domSanitizer.bypassSecurityTrustResourceUrl(
        URL.createObjectURL(file),
      ) as string;
    })
  }

  areAllFilesPDF(files: File[]): boolean {
    return files.every(file => file.type === 'application/pdf');
  }

  public mergePDFs(files: File[]): Observable<Blob> {
    return new Observable<Blob>((observer) => {
      setTimeout(async () => {
        const mergedPdf = await PDFDocument.create();

        for (const file of files) {
          const arrayBuffer = await file.arrayBuffer();
          const pdf = await PDFDocument.load(arrayBuffer);

          const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
          copiedPages.forEach(page => mergedPdf.addPage(page));
        }

        let fileBuffer = (await mergedPdf.save()).buffer;

        var result = new Blob([fileBuffer], { type: 'application/pdf' });
        observer.next(result);
        observer.complete();
        return result;
      }, 10)
    })
  }


}

